import { useRecoilValue } from 'recoil';

import { SchedulesNoResults } from '@/components/modules/schedules/no-results';
import { UserMessage } from '@/components/ui/user-message';
import { useFlightServices } from '@/hooks/use-flight-services';
import { ISO_DATE_TIME_FORMAT } from '@/lib/constants';
import { getLocationByIdState } from '@/state/locations';
import { ServicesApiRouteParams } from '@/types';
import { formatIsoDate } from '@/utils/format/date-time';
import { getPreferredIdentifier } from '@/utils/helpers';

import { FlightServicesListContent } from './content';
import { FlightServicesSkeleton } from './loading-skeleton';

export interface FlightServiceListProps {
  originId: string;
  destinationId: string;
  routes: ServicesApiRouteParams[];
  date: Date;
  aircraftTypes: string[];
  aircraft: string[];
}

const FlightServiceList = ({
  originId,
  destinationId,
  date,
  routes,
  aircraftTypes = [],
  aircraft = [],
}: FlightServiceListProps) => {
  const origin = useRecoilValue(getLocationByIdState(originId));
  const destination = useRecoilValue(getLocationByIdState(destinationId));
  const originIdentifier = getPreferredIdentifier(origin.identifiers)?.value || '';
  const destinationIdentifier = getPreferredIdentifier(destination.identifiers)?.value || '';

  const { flightServices, error, isLoading } = useFlightServices({
    o: originIdentifier,
    d: destinationIdentifier,
    date: formatIsoDate(date, ISO_DATE_TIME_FORMAT),
    routes,
    aircraftType: aircraftTypes.join(','),
    aircraft: aircraft.join(','),
  });

  if (error) return <UserMessage variant="error" heading={error} />;
  if (isLoading) return <FlightServicesSkeleton />;
  if (!isLoading && !error && !flightServices.length)
    return (
      <SchedulesNoResults
        message="We could note find any upcoming scheduled services on this route."
        secondaryMessage="Please try a different route. If this issue persists - let us know via the feedback button on this page."
      />
    );
  if (flightServices.length > 0) return <FlightServicesListContent flightServices={flightServices} />;

  return null;
};

export { FlightServiceList };
